import { memo, useEffect, useRef, useState } from "react";
import { Box, ButtonBase, Collapse, Typography, useTheme } from "@mui/material";
import { AltArrowDown, ArrowRightUp } from "@solar-icons/react/ssr";

import {
    mobileNavTransitionDuration,
    sideNavTransition,
} from "@/nav/navConstants";
import { NavItemIcon } from "@/nav/NavIcons";
import { getSideNavColors } from "@/nav/navTheme";
import { colorPalette } from "@/themes";
import { getOrgLogoUrl, useOrgLogoSrc } from "@/common/OrgLogo";

const getOrganizationSubtitle = (organization) => {
    const descriptor = [
        organization?.workspace_type,
        organization?.type,
        organization?.subscription?.name,
        organization?.subscription_name,
        organization?.subscription,
    ]
        .filter(Boolean)
        .find((value) => typeof value == "string");

    if (descriptor?.toLowerCase().includes("solo")) {
        return "Solo";
    }

    return "Team";
};

function MobileOrganizationSwitcher({
    activeOrganization,
    organizations,
    onSelectOrganization,
}) {
    const [open, setOpen] = useState(false);
    const theme = useTheme();
    const canSwitch = organizations.length > 1;
    const dropdownBackground =
        theme.palette.mode == "dark"
            ? colorPalette.grey.dark.three
            : colorPalette.grey.light.three;
    const dropdownShadow =
        theme.palette.mode == "dark"
            ? "0px 4px 50px rgba(0, 0, 0, 0.25)"
            : "0px 4px 50px rgba(0, 0, 0, 0.12)";
    const workspaceMenuId = "mobile-workspace-menu";

    if (!activeOrganization) {
        return null;
    }

    const subtitle = getOrganizationSubtitle(activeOrganization);

    return (
        <Box
            sx={{
                position: "relative",
                zIndex: 2,
                marginBottom: "26px",
            }}
        >
            <ButtonBase
                disableRipple
                onClick={() => {
                    if (canSwitch) {
                        setOpen((currentOpen) => !currentOpen);
                    }
                }}
                aria-controls={canSwitch && open ? workspaceMenuId : undefined}
                aria-expanded={canSwitch ? open : undefined}
                aria-haspopup={canSwitch ? "menu" : undefined}
                aria-label={`${activeOrganization.name} ${subtitle}`}
                sx={{
                    width: "100%",
                    height: 48,
                    marginBottom: "8px",
                    padding: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "6px",
                    borderRadius: "8px",
                    cursor: canSwitch ? "pointer" : "default",
                    textAlign: "left",
                    backgroundColor: "transparent",
                    WebkitTapHighlightColor: "transparent",
                    "&:hover": {
                        backgroundColor: "transparent",
                    },
                    "&:active, &.Mui-focusVisible": {
                        backgroundColor: "transparent",
                    },
                }}
            >
                <OrganizationAvatar organization={activeOrganization} mobile />
                <OrganizationText
                    mobile
                    title={activeOrganization.name}
                    subtitle={subtitle}
                />
                {canSwitch && (
                    <NavItemIcon
                        icon={AltArrowDown}
                        size={14}
                        sx={{
                            transform: open ? "rotate(180deg)" : "rotate(0deg)",
                            transformOrigin: "center",
                            transition: theme.transitions.create("transform", {
                                easing: theme.transitions.easing.easeOut,
                                duration: mobileNavTransitionDuration,
                            }),
                        }}
                    />
                )}
            </ButtonBase>

            <Collapse
                in={open}
                timeout={mobileNavTransitionDuration}
                easing={theme.transitions.easing.easeOut}
                unmountOnExit
                id={workspaceMenuId}
                role="menu"
                aria-label="Switch workspace"
                sx={{
                    position: "relative",
                    zIndex: 2,
                    overflow: "hidden",
                    borderRadius: "10px",
                    backgroundColor: dropdownBackground,
                    boxShadow: dropdownShadow,
                    "& .MuiCollapse-wrapper, & .MuiCollapse-wrapperInner": {
                        overflow: "hidden",
                    },
                }}
            >
                <Box
                    sx={{
                        display: "flow-root",
                    }}
                >
                    <Typography
                        sx={{
                            margin: "10px 14px 0",
                            color: colorPalette.universalGrey,
                            fontSize: 12,
                            fontWeight: 400,
                            lineHeight: "100%",
                        }}
                    >
                        Switch workspace
                    </Typography>
                    <Box sx={{ margin: "4px 6px 10px" }}>
                        {organizations.map((organization) => {
                            const active =
                                organization.id == activeOrganization.id;

                            return (
                                <OrganizationOption
                                    key={organization.id}
                                    mobile
                                    organization={organization}
                                    active={active}
                                    onSelect={() => {
                                        if (!active) {
                                            onSelectOrganization(organization);
                                            setOpen(false);
                                        }
                                    }}
                                />
                            );
                        })}
                    </Box>
                </Box>
            </Collapse>
        </Box>
    );
}

function DesktopOrganizationSwitcher({
    activeOrganization,
    collapsed,
    expanded,
    indicatorHovered,
    organizations,
    renderExpandedContent,
    onSelectOrganization,
}) {
    const [open, setOpen] = useState(false);
    const switcherRef = useRef(null);
    const theme = useTheme();
    const colors = getSideNavColors(theme);
    const canSwitch = organizations.length > 1;
    const subtitle = getOrganizationSubtitle(activeOrganization);
    const workspaceMenuId = "desktop-workspace-menu";

    useEffect(() => {
        let closeTimeout;

        if (collapsed) {
            closeTimeout = setTimeout(() => {
                setOpen(false);
            }, 0);
        }

        return () => clearTimeout(closeTimeout);
    }, [collapsed]);

    useEffect(() => {
        let closeTimeout;

        if (expanded && indicatorHovered && open) {
            closeTimeout = setTimeout(() => {
                setOpen(false);
            }, 0);
        }

        return () => clearTimeout(closeTimeout);
    }, [expanded, indicatorHovered, open]);

    useEffect(() => {
        if (!open) {
            return undefined;
        }

        const handleOutsidePointerDown = (event) => {
            if (switcherRef.current?.contains(event.target)) {
                return;
            }

            if (event.target.closest?.("[data-sidenav-toggle='true']")) {
                return;
            }

            setOpen(false);
        };

        document.addEventListener("pointerdown", handleOutsidePointerDown);

        return () => {
            document.removeEventListener(
                "pointerdown",
                handleOutsidePointerDown,
            );
        };
    }, [open]);

    if (!activeOrganization) {
        return null;
    }

    return (
        <Box
            ref={switcherRef}
            sx={{
                position: "relative",
                zIndex: 2,
                padding: collapsed ? "20px 10px 0" : "20px 12.5px 0",
                transition: `padding ${sideNavTransition}`,
            }}
        >
            <ButtonBase
                disableRipple
                onClick={() => {
                    if (canSwitch) {
                        setOpen((currentOpen) => !currentOpen);
                    }
                }}
                aria-controls={canSwitch && open ? workspaceMenuId : undefined}
                aria-expanded={canSwitch ? open : undefined}
                aria-haspopup={canSwitch ? "menu" : undefined}
                aria-label={
                    collapsed
                        ? `${activeOrganization.name} ${subtitle}`
                        : undefined
                }
                sx={{
                    width: collapsed ? 40 : 170,
                    height: collapsed ? 40 : 42,
                    padding: collapsed ? "6px" : "6px 8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    gap: "8px",
                    borderRadius: "7px",
                    overflow: "hidden",
                    transformOrigin: "top left",
                    cursor: canSwitch ? "pointer" : "default",
                    textAlign: "left",
                    backgroundColor: open
                        ? colors.orgHoverBackground
                        : "transparent",
                    color: colors.orgTitle,
                    transition: `width ${sideNavTransition}, height ${sideNavTransition}, padding ${sideNavTransition}, background-color 160ms ease`,
                    "&:hover": {
                        backgroundColor: colors.orgHoverBackground,
                    },
                    "&:active, &.Mui-focusVisible": {
                        backgroundColor: colors.orgHoverBackground,
                    },
                }}
            >
                <OrganizationAvatar
                    organization={activeOrganization}
                    size={collapsed ? 28 : 32}
                />
                {renderExpandedContent && (
                    <Box
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            opacity: expanded ? 1 : 0,
                            transition: `opacity ${sideNavTransition}`,
                        }}
                    >
                        <OrganizationText
                            title={activeOrganization.name}
                            subtitle={subtitle}
                            colors={colors}
                        />
                        {canSwitch && <DesktopChevron open={open} />}
                    </Box>
                )}
            </ButtonBase>

            {open && (
                <Box
                    id={workspaceMenuId}
                    role="menu"
                    aria-label="Switch workspace"
                    sx={{
                        position: "absolute",
                        top: 83,
                        left: 12,
                        zIndex: 4,
                        width: 298,
                        height: 144,
                        padding: 0,
                        boxSizing: "border-box",
                        overflowY: "auto",
                        borderRadius: "10px",
                        backgroundColor: colors.dropdownBackground,
                        boxShadow: colors.dropdownShadow,
                        scrollbarWidth: "none",
                        "&::-webkit-scrollbar": {
                            display: "none",
                        },
                    }}
                >
                    <Typography
                        sx={{
                            margin: "13px 14px 9px",
                            color: colors.subtitle,
                            fontSize: 12,
                            fontWeight: 400,
                            lineHeight: "100%",
                        }}
                    >
                        Switch workspace
                    </Typography>
                    <Box sx={{ margin: "0 6px 10px 6px" }}>
                        {organizations.map((organization) => {
                            const active =
                                organization.id == activeOrganization.id;

                            return (
                                <OrganizationOption
                                    key={organization.id}
                                    active={active}
                                    organization={organization}
                                    onSelect={() => {
                                        if (!active) {
                                            onSelectOrganization(organization);
                                            setOpen(false);
                                        }
                                    }}
                                />
                            );
                        })}
                    </Box>
                </Box>
            )}
        </Box>
    );
}

function OrganizationOption({
    active,
    mobile = false,
    organization,
    onSelect,
}) {
    const theme = useTheme();
    const colors = getSideNavColors(theme);
    const disabled = organization.paused;
    const isDark = theme.palette.mode == "dark";

    return (
        <ButtonBase
            disableRipple
            onClick={disabled ? undefined : onSelect}
            role="menuitemradio"
            aria-checked={active}
            aria-disabled={disabled || active}
            sx={{
                width: "100%",
                height: 48,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: mobile ? "6px" : "8px",
                padding: "8px",
                boxSizing: "border-box",
                borderRadius: "6px",
                textAlign: "left",
                opacity: disabled ? 0.5 : 1,
                cursor: active || disabled ? "default" : "pointer",
                backgroundColor: "transparent",
                color: mobile ? undefined : colors.orgTitle,
                WebkitTapHighlightColor: mobile ? "transparent" : undefined,
                "&:hover": {
                    backgroundColor: disabled
                        ? "transparent"
                        : colors.orgOptionHoverBackground,
                },
                "&:active, &.Mui-focusVisible": {
                    backgroundColor: disabled
                        ? "transparent"
                        : colors.orgOptionHoverBackground,
                },
            }}
        >
            <OrganizationAvatar organization={organization} mobile={mobile} />
            <OrganizationText
                mobile={mobile}
                title={organization.name}
                subtitle={getOrganizationSubtitle(organization)}
                colors={colors}
            />
            {active ? (
                <Box
                    component="span"
                    sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: mobile ? 20 : 26,
                        padding: mobile ? "6px 10px" : "0 13px",
                        boxSizing: "border-box",
                        borderRadius: "1000px",
                        border: mobile
                            ? `0.6px solid ${isDark ? colorPalette.green.light.main : colorPalette.green.dark.two}`
                            : `1px solid ${colorPalette.green.light.main}`,
                        backgroundColor: mobile
                            ? isDark
                                ? colorPalette.green.dark.three
                                : colorPalette.green.light.three
                            : colors.activeBadgeBackground,
                        color: mobile
                            ? isDark
                                ? colorPalette.green.light.main
                                : colorPalette.green.dark.two
                            : colors.activeBadgeText,
                        fontSize: mobile ? 11 : 12,
                        fontWeight: 400,
                        lineHeight: "100%",
                    }}
                >
                    Active
                </Box>
            ) : mobile ? (
                <NavItemIcon
                    color={isDark ? colorPalette.white : colorPalette.black}
                    icon={ArrowRightUp}
                    size={14}
                />
            ) : (
                <NavItemIcon icon={ArrowRightUp} size={16} />
            )}
        </ButtonBase>
    );
}

function OrganizationAvatar({ mobile = false, organization, size = 32 }) {
    const theme = useTheme();
    const colors = getSideNavColors(theme);
    const subtitle = getOrganizationSubtitle(organization);
    const hasLogo = !!getOrgLogoUrl(organization?.logo);
    const logoSrc = useOrgLogoSrc(organization?.logo);
    const isSolo = subtitle == "Solo";
    const fallbackBackgroundColor = mobile
        ? theme.palette.mode == "dark"
            ? isSolo
                ? colorPalette.grey.dark.one
                : colorPalette.grey.dark.two
            : isSolo
              ? colorPalette.grey.light.one
              : colorPalette.grey.light.two
        : isSolo
          ? colors.avatarBackground
          : colors.teamAvatarBackground;

    return (
        <Box
            sx={{
                width: size,
                height: size,
                flex: `0 0 ${size}px`,
                display: "grid",
                placeItems: "center",
                overflow: "hidden",
                borderRadius: "6px",
                backgroundColor: hasLogo
                    ? colorPalette.white
                    : fallbackBackgroundColor,
                transition: mobile
                    ? undefined
                    : `width ${sideNavTransition}, height ${sideNavTransition}, flex-basis ${sideNavTransition}`,
            }}
        >
            <Box
                component="img"
                alt=""
                aria-hidden="true"
                src={logoSrc}
                sx={{
                    display: "block",
                    width: size,
                    height: size,
                    objectFit: "contain",
                    transition: mobile
                        ? undefined
                        : `width ${sideNavTransition}, height ${sideNavTransition}`,
                }}
            />
        </Box>
    );
}

function OrganizationText({ colors, mobile = false, title, subtitle }) {
    const theme = useTheme();

    if (mobile) {
        return (
            <Box
                sx={{
                    flex: 1,
                    minWidth: 0,
                    height: 32,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    gap: "4px",
                }}
            >
                <Typography
                    noWrap
                    sx={{
                        color: theme.palette.text.primary,
                        fontSize: 14,
                        fontWeight: 400,
                        lineHeight: "100%",
                    }}
                >
                    {title}
                </Typography>
                <Typography
                    noWrap
                    sx={{
                        color: colorPalette.universalGrey,
                        fontSize: 12,
                        fontWeight: 400,
                        lineHeight: "100%",
                    }}
                >
                    {subtitle}
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                flex: 1,
                minWidth: 0,
                height: 32,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
            }}
        >
            <Typography
                noWrap
                sx={{
                    height: 17,
                    color: colors.orgTitle,
                    display: "flex",
                    alignItems: "center",
                    fontSize: 14,
                    fontWeight: 400,
                    lineHeight: "17px",
                    overflow: "hidden",
                }}
            >
                {title}
            </Typography>
            <Typography
                noWrap
                sx={{
                    height: 15,
                    color: colors.subtitle,
                    display: "flex",
                    alignItems: "center",
                    fontSize: 12,
                    fontWeight: 400,
                    lineHeight: "15px",
                    overflow: "hidden",
                }}
            >
                {subtitle}
            </Typography>
        </Box>
    );
}

function DesktopChevron({ open, size = 13 }) {
    return (
        <NavItemIcon
            icon={AltArrowDown}
            size={size}
            sx={{
                transform: open ? "rotate(180deg)" : "rotate(0deg)",
                transformOrigin: "center",
                transition: `transform ${sideNavTransition}`,
            }}
        />
    );
}

const OrganizationSwitcher = {
    Desktop: memo(DesktopOrganizationSwitcher),
    Mobile: memo(MobileOrganizationSwitcher),
};

export { getOrganizationSubtitle };
export default OrganizationSwitcher;
